import { Plus, Pencil, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { CostChip } from "./DemoRecipeCard";
import { DAYS, slotLabelKey, slotRatio } from "@/lib/demo";

function Slot({ day, slot, recipe, persons, onPick, onRemove, onView }) {
  const { t, tv } = useLang();
  const id = `demo-slot-${day}-${slot}`;
  if (!recipe) {
    return (
      <button type="button" onClick={() => onPick({ day, slot })} data-testid={id}
        className="flex w-full items-center justify-between gap-3 rounded-xl border-2 border-dashed border-primary/40 bg-white/60 p-3 text-left transition-colors hover:border-primary hover:bg-chenia-rosesoft/30">
        <span><span className="block text-[11px] font-medium text-muted-foreground">{t(slotLabelKey(slot))}</span><span className="text-sm font-semibold text-primary">{t("dm_to_choose")}</span></span>
        <Plus className="h-4 w-4 text-primary" />
      </button>
    );
  }
  return (
    <div className="flex gap-3 rounded-xl bg-white/80 p-2.5" data-testid={id} data-recipe={recipe.id}>
      <button type="button" onClick={() => onView(recipe)} className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary"><img src={recipe.image} alt="" className="h-full w-full object-cover" /></button>
      <div className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium text-muted-foreground">{t(slotLabelKey(slot))}</span>
        <button type="button" onClick={() => onView(recipe)} className="line-clamp-2 text-left text-sm font-semibold leading-snug hover:underline">{tv(recipe.name)}</button>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <CostChip cost={recipe.cost} portions={slotRatio(slot) * (persons || 1)} />
          <button type="button" onClick={() => onPick({ day, slot })} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] hover:border-primary" data-testid={`${id}-change`}><Pencil className="h-3 w-3" />{t("dm_change")}</button>
          <button type="button" onClick={() => onRemove(day, slot)} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-destructive hover:text-destructive" data-testid={`${id}-remove`}><X className="h-3 w-3" />{t("dm_remove")}</button>
        </div>
      </div>
    </div>
  );
}

export function DemoMenu({ plan, days, slots, persons, recipesById, onPick, onRemove, onView, onReset }) {
  const { t } = useLang();
  const total = days.length * slots.length;
  const chosen = days.reduce((n, d) => n + slots.filter((s) => recipesById[plan[d]?.[s]]).length, 0);
  return (
    <section className="mt-5 chenia-card p-5 sm:p-8" aria-label={t("dm_tab_menu")} data-testid="demo-menu">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold">{t("dm_menu_title")}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t("dm_menu_intro")}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold" data-testid="demo-menu-progress">{t("dm_menu_progress", { chosen, total })}</p>
          {chosen > 0 && <Button variant="ghost" size="sm" onClick={onReset} className="mt-1 rounded-full text-xs" data-testid="demo-menu-reset">{t("dm_reset")}</Button>}
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {DAYS.map((day) => days.includes(day) ? (
          <article key={day} className="rounded-2xl bg-secondary/50 p-3" data-testid={`demo-day-card-${day}`}>
            <h3 className="px-1 font-semibold">{t(`demo_day_${day}`)}</h3>
            <div className="mt-2 space-y-2">{slots.map((slot) => <Slot persons={persons} key={slot} day={day} slot={slot} recipe={recipesById[plan[day]?.[slot]]} onPick={onPick} onRemove={onRemove} onView={onView} />)}</div>
          </article>
        ) : <article key={day} className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">{t(`demo_day_${day}`)} · {t("dm_day_off")}</article>)}
      </div>
    </section>
  );
}
